import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { onMilestone, type MilestoneEvent } from '../net/liveSession';
import { useGameStore } from '../store/gameStore';
import { audio } from '../game/audio/AudioEngine';
import { buzz, HAPTIC } from '../util/haptics';

/**
 * MilestoneToast — pops a celebratory chip when the player's mass
 * crosses a server-side milestone (1K / 2.5K / 5K / 10K / 25K).
 *
 * The server fires exactly once per milestone per life, so there's
 * no risk of spam. The toast queues up to 2 simultaneous (the player
 * could cross two thresholds in one bite if they're eating a giant
 * corpse drop), but anything beyond 2 is dropped to avoid a snake
 * of toasts marching down the screen.
 *
 * Each toast: 2.2 s lifetime, gold tint, scale-in + drift-up,
 * positioned slightly above-center so it doesn't compete with the
 * mass counter or leaderboard. Also plays a chime in the player's
 * own hue so each milestone feels like a personal high-five.
 */
interface ActiveToast {
  id: number;
  mass: number;
}

let nextToastId = 1;
const MAX_VISIBLE = 2;

function labelFor(mass: number): { headline: string; sub: string } {
  switch (mass) {
    // Early "starter" milestones — punchy upbeat micro-rewards that
    // give brand-new players a dopamine hit in the 10-30 s window.
    case 100:   return { headline: '100',    sub: 'first bite!' };
    case 250:   return { headline: '250',    sub: 'rolling' };
    case 500:   return { headline: '500',    sub: 'mid-pack' };
    case 1000:  return { headline: '1,000',  sub: 'first thousand' };
    case 2500:  return { headline: '2,500',  sub: 'climbing' };
    case 5000:  return { headline: '5,000',  sub: 'heavyweight' };
    case 10000: return { headline: '10,000', sub: 'apex predator' };
    case 25000: return { headline: '25,000', sub: 'unstoppable' };
    default:    return { headline: mass.toLocaleString(), sub: 'mass milestone' };
  }
}

export function MilestoneToast() {
  const [active, setActive] = useState<ActiveToast[]>([]);
  const playerHue = useGameStore((s) => s.hud.playerHue);
  // Suppress while dead — a lingering milestone toast would overlap
  // (and visually fight) the death modal's final-mass headline.
  const alive = useGameStore((s) => s.hud.alive);

  useEffect(() => {
    return onMilestone((e: MilestoneEvent) => {
      setActive((prev) => {
        // Drop oldest if at the cap so the newest milestone always shows.
        const trimmed = prev.length >= MAX_VISIBLE ? prev.slice(1) : prev;
        return [...trimmed, { id: nextToastId++, mass: e.mass }];
      });
      // Audio reaction — reuse the existing `spawn()` chord at low
      // volume; it's the right "positive accomplishment" tone and
      // means no new sample to ship. Engine's audio.unlock() in
      // submitPlay() guarantees this fires even on iOS.
      try { audio.spawn(playerHue); } catch { /* noop */ }
      // Mobile-only physical pulse — desktops silently no-op.
      buzz(HAPTIC.milestone);
      // 2.2 s self-clear so toasts don't pile up if the player has
      // a really good run.
      const idAtFire = nextToastId - 1;
      window.setTimeout(() => {
        setActive((prev) => prev.filter((t) => t.id !== idAtFire));
      }, 2200);
    });
  }, [playerHue]);

  if (!alive) return null;

  return (
    <div className="absolute top-32 left-1/2 -translate-x-1/2 z-30 pointer-events-none flex flex-col items-center gap-2">
      <AnimatePresence>
        {active.map((t, i) => {
          const { headline, sub } = labelFor(t.mass);
          return (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, scale: 0.7, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: -12, transition: { duration: 0.25 } }}
              transition={{ type: 'spring', stiffness: 380, damping: 22 }}
              style={{
                background: 'linear-gradient(180deg, rgba(252,211,77,0.28) 0%, rgba(251,191,36,0.18) 100%)',
                border: '1px solid rgba(252,211,77,0.65)',
                boxShadow:
                  '0 0 24px rgba(252,211,77,0.35), 0 0 60px rgba(252,211,77,0.18), inset 0 1px 0 rgba(255,255,255,0.18)',
                opacity: 1 - i * 0.18, // older toasts fade slightly so the newest dominates
              }}
              className="px-5 py-2.5 rounded-2xl text-center backdrop-blur-md"
            >
              <div className="flex items-center justify-center gap-2">
                <span className="text-base" aria-hidden>⭐</span>
                <span
                  className="text-2xl font-extrabold tabular-nums"
                  style={{
                    fontFamily: '"Inter", "Space Grotesk", system-ui, sans-serif',
                    color: 'rgb(254 240 138)',
                    textShadow: '0 0 12px rgba(252,211,77,0.85), 0 0 28px rgba(251,191,36,0.55)',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {headline}
                </span>
              </div>
              <div className="text-[9px] uppercase tracking-[0.34em] text-amber-200/80 font-bold mt-0.5">
                {sub}
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
